import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiClient } from '../api/client';
import { translations } from '../i18n/translations';

const LanguageContext = createContext();

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

export const LanguageProvider = ({ children }) => {
    // Default to 'ru' initially until we fetch from backend or detect from Telegram
    const [language, setLanguage] = useState('ru');
    const [vibrationEnabled, setVibrationEnabled] = useState(true);
    const [isLoaded, setIsLoaded] = useState(false);

    // Initial load
    useEffect(() => {
        const initSettings = async () => {
            try {
                // Try to get settings from backend
                // If backend returns null for language, it means auto.
                // But for frontend context we need a concrete value.
                const settings = await apiClient.getSettings();

                let lang = settings.language;

                // If backend didn't have a preference (or returned null/auto), try Telegram
                if (!lang) {
                    const tgLang = window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code;
                    if (tgLang) {
                        if (tgLang === 'ru' || tgLang === 'uk') {
                            lang = tgLang;
                        } else {
                            lang = 'en';
                        }
                    } else {
                        lang = 'ru'; // Fallback default
                    }
                }

                // Validate support
                if (!translations[lang]) lang = 'en';

                setLanguage(lang);
                if (settings.vibration_enabled !== undefined && settings.vibration_enabled !== null) {
                    setVibrationEnabled(settings.vibration_enabled);
                }
            } catch (error) {
                console.error("Failed to load settings", error);
                // Fallback to Telegram param or default
                const tgLang = window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code;
                setLanguage((tgLang === 'ru' || tgLang === 'uk') ? tgLang : 'en');
            } finally {
                setIsLoaded(true);
            }
        };

        if (window.Telegram?.WebApp) {
            window.Telegram.WebApp.ready();
        }

        initSettings();
    }, []);

    const updateLanguage = async (newLang) => {
        setLanguage(newLang); // Optimistic update
        try {
            await apiClient.updateSettings({ language: newLang });
        } catch (error) {
            console.error("Failed to sync language", error);
        }
    };

    const toggleVibration = async () => {
        const newVal = !vibrationEnabled;
        setVibrationEnabled(newVal); // Optimistic
        if (newVal && window.Telegram?.WebApp?.HapticFeedback) {
            window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
        }
        try {
            await apiClient.updateSettings({ vibration_enabled: newVal });
        } catch (error) {
            console.error("Failed to sync vibration", error);
        }
    };

    const t = (key) => {
        const dict = translations[language] || translations['en'];
        return dict[key] || key;
    };

    return (
        <LanguageContext.Provider value={{
            language,
            setLanguage: updateLanguage,
            vibrationEnabled,
            toggleVibration,
            t,
            isLoaded
        }}>
            {children}
        </LanguageContext.Provider>
    );
};
